import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import {
  getBusinessImprovementPlanGeneralData,
  getBusinessPlanDetails,
  getBusinessPlanReport,
} from "../_redux/BusinessImprovementPlanActions";
import BusinessImprovementPlanHeader from "../BusinessImprovementPlanHeader/BusinessImprovementPlanHeader";
import { ReactSVG } from "react-svg";
import {
  BigBoldText,
  PrimaryBoldText,
  SmallLightText,
  SmallNormalText,
} from "../../../../utils/typographyUtils";
import { ChevronRight, PlusIcon, Power } from "lucide-react";
import StandardButton from "../../../shared/formComponents/StandardButton/StandardButton";
import ButtonIcon from "../../../shared/formComponents/StandardButton/ButtonIcon";
import classNames from "classnames";
import { ConditionalRender } from "../../../shared/ConditionalRender/ConditionalRender";
import { StandardTooltip } from "../../../shared/StandardTooltip/StandardTooltip";
import {
  convertBlobToExcel,
  getDaysLeftColor,
  getProgressBarColour,
} from "../../../../utils/generalUtils";
import StandardCircularProgressBar from "../../../shared/StandardCircularProgressBar/StandardCircularProgressBar";
import { RelativeLoadingAnimation } from "../../../../base/LoadingAnimation";
import "../business-improvement-plan.scss";
import { UnpublishedPlanObjectiveAccordion } from "./UnpubishedPlanObjectiveAccordion/UnpublishedPlanObjectiveAccordion";
import { PublishedPlanObjectiveAccordion } from "./PublishedPlanObjectiveAccordion/PublishedPlanObjectiveAccordion";

export default function BusinessImprovementPlanView() {
  const dispatch = useDispatch();
  const { planId, userId } = useParams();
  const [businessPlan, setBusinessPlan] = useState(null);
  const [businessPlanGeneralData, setBusinessPlanGeneralData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("Company");
  const [downloadingReport, setDownloadingReport] = useState(false);
  const tabs = ["Company"];

  useEffect(() => {
    handleGetBusinessPlanDetails();
    handleGetBusinessPlanGeneralData();
  }, []);

  const handleGetBusinessPlanDetails = async () => {
    try {
      const resp = await dispatch(getBusinessPlanDetails(planId, userId));
      if (resp) {
        setBusinessPlan(resp);
      }
    } catch (e) {
      console.log(e);
    } finally {
      setIsLoading(false);
    }
  };

  const handleGetBusinessPlanGeneralData = async () => {
    try {
      const resp = await dispatch(
        getBusinessImprovementPlanGeneralData(userId)
      );
      if (resp) {
        setBusinessPlanGeneralData(resp.data);
      }
    } catch (e) {
      console.log(e);
    }
  };

  const handleGetBusinessPlanReport = async () => {
    setDownloadingReport(true);
    try {
      const resp = await dispatch(getBusinessPlanReport(planId, userId));
      if (resp) {
        convertBlobToExcel(
          resp,
          undefined,
          `Business Plan Report - ${businessPlan.quarter}`
        );
      }
    } catch (e) {
      console.log(e);
    } finally {
      setDownloadingReport(false);
    }
  };

  const renderHeaderCtas = () => {
    if (businessPlan?.isArchived) {
      return (
        <div className="d-flex align-items-center bg-white px-3 py-2 rounded shadow">
          <img
            src="/assets/images/bip_archived.svg"
            className="me-1 minus-margin-top-4"
          />
          <SmallLightText>
            Archived by{" "}
            <PrimaryBoldText>{businessPlan?.archivedBy}</PrimaryBoldText> on{" "}
            <PrimaryBoldText>{businessPlan.archivedAt}</PrimaryBoldText>
          </SmallLightText>
        </div>
      );
    }
    if (businessPlan?.isClosed) {
      return (
        <div className="d-flex align-items-center bg-white px-3 py-2 rounded shadow">
          <Power
            color="#FA6C6C"
            className="font-weight-bold me-1 minus-margin-top-2"
            height={15}
            width={15}
          />
          <SmallLightText>
            Closed Out by{" "}
            <PrimaryBoldText>{businessPlan.closedBy}</PrimaryBoldText> on{" "}
            <PrimaryBoldText>{businessPlan.closedAt}</PrimaryBoldText>
          </SmallLightText>
        </div>
      );
    }
    if (businessPlan.isPublished) {
      return (
        <div className="d-flex align-items-center">
          <StandardButton
            color="btn-rounded-success"
            className="mt-3 mt-lg-0 ms-auto"
            onClick={() => {}}
            icon={<i class="fa-chevron-right fa-solid ms-3"></i>}
            text=" Close Out Plan"
            disabled={true}
            size="btn-md"
          />
        </div>
      );
    }
    return (
      <StandardButton
        color="btn-rounded-success"
        onClick={() => {}}
        icon={<ButtonIcon icon={ChevronRight} />}
        text="Share Plan"
        disabled={true}
        className="ms-auto"
        size="btn-md"
      />
    );
  };

  const renderLabelValue = (imgSrc, label, value) => {
    if (!businessPlan.isPublished) return null;
    return (
      <div className="d-flex align-items-center label-value-div me-3 me-md-0 ms-md-3 mt-3 mt-md-0">
        <div className="label d-flex align-items-center">
          <img src={imgSrc} className="me-2" />
          <SmallLightText>{label}</SmallLightText>
        </div>
        <div className="d-flex align-items-center value">{value}</div>
      </div>
    );
  };

  if (isLoading) {
    return (
      <div className="bg-white p-5">
        <RelativeLoadingAnimation />
      </div>
    );
  }
  return (
    <>
      <BusinessImprovementPlanHeader />
      <section id="business-improvement-plan-view-section">
        <div className="d-flex flex-column bg-white my-2 cs-br-8">
          <div
            className={`d-flex flex-column flex-lg-row align-items-lg-center border-bottom justify-content-between p-3 ${
              businessPlan.isPublished
                ? "bg-base-primary rounded-top rounded-top-right"
                : ""
            }`}
          >
            <div className={`d-flex flex-column`}>
              <div
                className={`${
                  businessPlan.isPublished ? "d-flex align-items-center" : ""
                }`}
              >
                {businessPlan.isPublished ? (
                  <ReactSVG
                    className="me-2"
                    src="/assets/images/bip_banner_white.svg"
                  />
                ) : null}
                <BigBoldText
                  classNames={`d-block ${
                    businessPlan.isPublished ? "text-white" : ""
                  }`}
                >
                  {" "}
                  Scalability Plan | {businessPlan.quarter}
                </BigBoldText>
              </div>
              {businessPlan.isPublished ? null : (
                <SmallNormalText classNames="d-block">
                  This draft plan is AI-generated as a useful starting point.
                  Review and customise the Objectives, Key Results, and
                  Initiatives your particular business context and priorities.
                  When you're sure of your OKRs, click Share Plan to align your
                  team and start executing.
                </SmallNormalText>
              )}
            </div>
            <div className="text-end mt-3 mt-lg-0 mt-md-0 bip-actions">
              {renderHeaderCtas()}
            </div>
          </div>
          <div className="d-flex flex-column flex-md-row align-items-md-center justify-content-md-between p-3 pb-0">
            <div className="d-flex align-items-center">
              <div
                className="d-flex align-items-center"
                id="business-plan-tabs"
              >
                {tabs.map((tab) => {
                  let tabClasses = classNames(
                    "me-2 text-center cursor-pointer business-plan-tab",
                    {
                      "active-tab text-white": tab === activeTab,
                    }
                  );
                  return (
                    <div
                      className={tabClasses}
                      onClick={() => setActiveTab(tab)}
                    >
                      {tab}
                    </div>
                  );
                })}
              </div>
            </div>
            {businessPlan.isPublished ? (
              <div className="d-flex align-items-center">
                <div>
                  {renderLabelValue(
                    "/assets/images/primary-round-icon.svg",
                    "Total Score",
                    <SmallLightText disabled={downloadingReport}>
                      {businessPlan?.totalScore}%
                    </SmallLightText>
                  )}
                </div>

                {/* <ConditionalRender
                  condition={!businessPlan.isClosed && !businessPlan.isArchived}
                >
                  <StandardTooltip
                    tooltTipContent={
                      <span className="text-black tooltip-information">
                        {businessPlan.GNRQuarterlyRhythmStarted ? (
                          <div>
                            If you need to link the Scalability Plan in Goals &
                            Rhythms, please download the Excel file.
                          </div>
                        ) : (
                          <div>
                            You can use Scalability Plan Excel file in your
                            Goals & Rhythms to link it with your OKR.
                          </div>
                        )}
                      </span>
                    }
                  >
                    <div>
                      <StandardButton
                        color="btn-outline-brand-blue-new"
                        disabled={downloadingReport}
                        isLoading={downloadingReport}
                        darkLoader={true}
                        onClick={() => {
                          handleGetBusinessPlanReport();
                        }}
                        className="py-1"
                        icon={
                          <ReactSVG src="/assets/images/download_primary.svg" />
                        }
                        reversed={true}
                        text="Download Plan"
                      />
                    </div>
                  </StandardTooltip>
                </ConditionalRender> */}
                <ConditionalRender
                  condition={!businessPlan.isClosed && !businessPlan.isArchived}
                >
                  <StandardTooltip
                    tooltTipContent={
                      <span className="text-black tooltip-information">
                        {businessPlan.GNRQuarterlyRhythmStarted ? (
                          <div>
                            If you need to link the Scalability Plan in Goals &
                            Rhythms, please download the Excel file.
                          </div>
                        ) : (
                          <div>
                            You can use Scalability Plan Excel file in your
                            Goals & Rhythms to link it with your OKR.
                          </div>
                        )}
                      </span>
                    }
                  >
                    <div
                      className="cursor-pointer"
                      onClick={() => {
                        handleGetBusinessPlanReport();
                      }}
                      disabled={downloadingReport}
                    >
                      {renderLabelValue(
                        "/assets/images/download_primary.svg",
                        "",
                        <>
                          <SmallLightText>Plan</SmallLightText>
                        </>
                      )}
                    </div>
                  </StandardTooltip>
                </ConditionalRender>
                {renderLabelValue(
                  "/assets/images/bip_okr_progress.svg",
                  "OKR Progress",
                  <>
                    <StandardCircularProgressBar
                      radius={50}
                      height={25}
                      thresholdValue={0}
                      progress={
                        (businessPlan.completedObjectives /
                          businessPlan.totalObjectives) *
                        100
                      }
                      strokeWidth={10}
                      strokeColor={getProgressBarColour(
                        (businessPlan.completedObjectives /
                          businessPlan.totalObjectives) *
                          100
                      )}
                      trackStrokeWidth={6}
                      pointerRadius={6}
                      pointerStrokeWidth={1}
                      pointerStrokeColor="#333"
                      textColor={getProgressBarColour(
                        (businessPlan.completedObjectives /
                          businessPlan.totalObjectives) *
                          100
                      )}
                    />
                    <span
                      className="font-weight-bold me-1"
                      style={{
                        color: getProgressBarColour(
                          (businessPlan.completedObjectives /
                            businessPlan.totalObjectives) *
                            100
                        ),
                      }}
                      classNames={"me-1"}
                    >
                      {businessPlan.completedObjectives}
                    </span>
                    <SmallLightText>
                      {" "}
                      out of {businessPlan.totalObjectives}
                    </SmallLightText>
                  </>
                )}
                {businessPlan?.isArchived ? null : businessPlan.isClosed ? (
                  renderLabelValue(
                    "",
                    "BMIX",
                    <PrimaryBoldText>
                      {businessPlan.endingBmiScore ?? "-"}
                    </PrimaryBoldText>
                  )
                ) : businessPlan.daysLeft > 0 ? (
                  renderLabelValue(
                    "/assets/images/bip_calendar.svg",
                    "Days Left",
                    <span
                      className="font-weight-bold"
                      style={{
                        color: getDaysLeftColor(businessPlan.daysLeft),
                      }}
                    >
                      {businessPlan.daysLeft}
                    </span>
                  )
                ) : (
                  <div className="d-flex align-items-center label-value-div me-3 me-md-0 ms-md-3 mt-3 mt-md-0">
                    <div className="label d-flex align-items-center bg-white">
                      <img
                        src="/assets/images/bip_calender_overdue.svg"
                        className="me-2"
                      />
                      <span
                        className="font-weight-bold"
                        style={{
                          color: getDaysLeftColor(businessPlan.daysLeft),
                        }}
                      >
                        Overdue
                      </span>
                    </div>
                  </div>
                )}
              </div>
            ) : null}
            <ConditionalRender
              condition={businessPlan.latest_bip_state === "Draft"}
            >
              <StandardButton
                color="btn-outline-success"
                className="ms-3 py-1 font-weight-normal"
                reversed
                icon={
                  <PlusIcon
                    viewBox="0 0 24 26"
                    size={18}
                    height={18}
                    width={18}
                  />
                }
                text="Add X-Ray Recommendation"
                disabled={true}
                // onClick={() => setShowAddObjectivesPopup(true)}
              />
            </ConditionalRender>
          </div>

          <div className="d-flex flex-column">
            {businessPlan.isPublished ? (
              <PublishedPlanObjectiveAccordion
                businessPlanId={planId}
                isViewMode={true}
                isPlanPublished={businessPlan.isPublished}
                callBack={handleGetBusinessPlanDetails}
                allowDelete={businessPlan.objectives.length > 1}
                objectives={businessPlan.objectives}
                showActions={businessPlan.showActions}
              />
            ) : (
              <UnpublishedPlanObjectiveAccordion
                callBack={handleGetBusinessPlanDetails}
                objectives={businessPlan.objectives}
                businessPlanId={planId}
                showActions={businessPlan.showActions}
              />
            )}

            <div className="d-flex pb-3">
              <ConditionalRender
                condition={businessPlan.latest_bip_state === "Published"}
              >
                <StandardButton
                  color="btn-outline-success"
                  className="ms-3 py-1 font-weight-normal"
                  reversed
                  icon={
                    <PlusIcon
                      viewBox="0 0 24 26"
                      size={18}
                      height={18}
                      width={18}
                    />
                  }
                  text="Add New Objective"
                  onClick={() => {}}
                  disabled={true}
                />
              </ConditionalRender>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
